import {Path} from 'react-hook-form';
import {TallyInputFieldErrorCode} from 'tally-validation';

import {ErrorMessages, HandleValidationErrorParams} from './handleValidationError.types';

export const getErrorMessageForErrorCode = ({
  errorCode,
  errorMessages = {},
}: {
  errorCode: TallyInputFieldErrorCode;
  errorMessages?: {[key in TallyInputFieldErrorCode]?: string};
}) => {
  switch (errorCode) {
    case TallyInputFieldErrorCode.MISSING:
      return errorMessages?.[TallyInputFieldErrorCode.MISSING] || 'This field is required';
    case TallyInputFieldErrorCode.DUPLICATE:
      return (
        errorMessages?.[TallyInputFieldErrorCode.DUPLICATE] ||
        'This fieldʼs value is already associated with a user'
      );
    case TallyInputFieldErrorCode.MISMATCH:
      return errorMessages?.[TallyInputFieldErrorCode.MISMATCH] || 'This fieldʼs value must match';
    case TallyInputFieldErrorCode.TOO_SHORT:
      return (
        errorMessages?.[TallyInputFieldErrorCode.TOO_SHORT] || 'This fieldʼs value is too short'
      );
    case TallyInputFieldErrorCode.INVALID:
    default:
      return errorMessages?.[TallyInputFieldErrorCode.INVALID] || 'This fieldʼs value is invalid';
  }
};

/**
 * This is a generic function for handling validation errors returned by the
 * API, and mapping them to a corresponding input field via the `setError`
 * function return by `react-hook-form's `useForm` hook.
 *
 * The `setError` function for the given form must be provided as an argument to
 * this `handleValidationError`, and the field names provided to the `useForm`
 * hook must match the `invalidFields` that may be reported by the API.
 *
 * If no errors with the "Invalid Input" classCode are found, an error will be
 * set on the 'form' fieldvalue instead, which, when used with the base `Form`
 * component, will allow form-level errors to be displayed.
 *
 * Error messages may be customized by passing an `errorMessages` object
 * with keys corresponding to the FieldValues and TallyInputFieldErrorCode.
 */
export const handleValidationError = <T extends {form?: string}>({
  error,
  errorMessages = {form: 'Please check the entered data and try again.'} as ErrorMessages<T>,
  setError,
}: HandleValidationErrorParams<T>) => {
  const retryLimitExceededError =
    error?.graphQLErrors?.find(error => {
      return (
        error.extensions.error.classCode === 'SERVICE_FAILED' &&
        (error.extensions.error.serviceErrors?.['klaviyoApiService'] || []).includes(
          'retry-limit-exceeded',
        )
      );
    }) || null;
  if (retryLimitExceededError) {
    setError?.('form' as Path<T>, {message: errorMessages?.rateLimit});
    return;
  }

  const validationErrors =
    error?.graphQLErrors?.filter(
      ({extensions}) => extensions.error.classCode === 'INVALID_INPUT',
    ) || [];

  if (validationErrors.length) {
    validationErrors.forEach(({extensions}) => {
      const invalidKeys = Object.keys(extensions.error?.invalidFields) as unknown as Array<Path<T>>;
      invalidKeys.forEach(key => {
        setError(key, {
          message: getErrorMessageForErrorCode({
            errorCode: extensions.error.invalidFields[key][0],
            errorMessages: errorMessages[key],
          }),
        });
      });
    });
  } else {
    setError?.('form' as Path<T>, {message: errorMessages.form});
  }
};
